import { Box, HStack, SimpleGrid, Tag, Text } from '@chakra-ui/react';
import { dateTimeTransformer } from 'helpers/dateTransformer';
import { useTranslation } from 'react-i18next';

import { type WorkshopUpdateRequest,type WorkshopVerificationRequest } from '../types';

type VerificationDetailsGridProps = {
  verificationData: WorkshopVerificationRequest;
  updateRequest: WorkshopUpdateRequest | undefined;
  durNotCompleted: boolean;
  isStep1: boolean;
};

type DetailFieldProps = {
  label: string;
  currentValue: string | undefined;
  newValue?: string | undefined;
  oldValue?: string | undefined;
  showDiff: boolean;
  translate?: (val: string) => string;
};

const DetailField = ({ label, currentValue, newValue, oldValue, showDiff, translate }: DetailFieldProps) => {
  const displayNew = translate && newValue ? translate(newValue) : newValue;
  const displayCurrent = translate && currentValue ? translate(currentValue) : currentValue;
  const displayOld = translate && oldValue ? translate(oldValue) : oldValue;

  return (
    <HStack flexWrap="wrap">
      <Text fontWeight="600">{label}:</Text>
      {newValue ? (
        <Text color="blue.500" fontWeight="semibold">
          {displayNew ?? '-'}
        </Text>
      ) : (
        <Text>{displayCurrent ?? '-'}</Text>
      )}
      {newValue && showDiff && <Text as="s">{displayOld}</Text>}
    </HStack>
  );
};

const VerificationDetailsGrid = ({ verificationData, updateRequest, durNotCompleted, isStep1 }: VerificationDetailsGridProps) => {
  const { t } = useTranslation();
  const workshop = verificationData.workshop;
  const vatInfo = workshop?.vat_validation_information;
  const newValues = updateRequest?.new_values;
  const oldValues = updateRequest?.old_values;
  const showDiff = durNotCompleted;

  return (
    <HStack w="100%">
      <SimpleGrid columns={{ lg: 1, xl: 2, '2xl': 3 }} columnGap="24" rowGap="6" width="100%">
        <HStack>
          <Text fontWeight="600">Submitted at:</Text>
          <Text>
            {dateTimeTransformer(isStep1 ? (verificationData.step_1_submitted_at ?? '') : (verificationData.step_2_submitted_at ?? '')) ?? '-'}
          </Text>
        </HStack>

        <HStack>
          <Text fontWeight="600">{t('forms:created_at.label')}:</Text>
          <Text>{dateTimeTransformer(verificationData.created_at ?? '') ?? '-'}</Text>
        </HStack>

        <HStack>
          <Text fontWeight="600">{t('forms:processed_at.label')}:</Text>
          <Text>{dateTimeTransformer(verificationData.processed_at) ?? '-'}</Text>
        </HStack>

        <DetailField
          label={t('workshop:name.label')}
          currentValue={workshop?.name}
          newValue={newValues?.name}
          oldValue={oldValues?.name}
          showDiff={showDiff}
        />

        <Box display="flex" flexDir="column">
          <HStack marginBottom={1}>
            <Text fontWeight="600">{t('workshop:vat_number.label')}: </Text>
            {newValues?.vat_number ? (
              <Text color="blue.500" fontWeight="semibold">
                {newValues.vat_number ?? '-'}
              </Text>
            ) : (
              <Text>{workshop?.vat_number ?? '-'}</Text>
            )}
          </HStack>
          {newValues?.vat_number && showDiff && <Text as="s">{oldValues?.vat_number}</Text>}
          {(vatInfo?.name || vatInfo?.address) && (
            <Tag size="sm" variant="solid" bgColor={vatInfo?.valid ? 'green' : 'orange.200'}>
              <Text as="div">{vatInfo?.name}</Text>, <Text as="div">{vatInfo?.address}</Text>
            </Tag>
          )}
        </Box>

        <DetailField
          label={t('workshop:language.label')}
          currentValue={workshop?.language}
          newValue={newValues?.language}
          oldValue={oldValues?.language}
          showDiff={showDiff}
          translate={(val) => t(`location:languages.${val.toUpperCase()}`)}
        />

        <DetailField
          label={t('workshop:address.label')}
          currentValue={workshop?.address}
          newValue={newValues?.address}
          oldValue={oldValues?.address}
          showDiff={showDiff}
        />

        <DetailField
          label={t('workshop:city.label')}
          currentValue={workshop?.city}
          newValue={newValues?.city}
          oldValue={oldValues?.city}
          showDiff={showDiff}
        />

        <DetailField
          label={t('workshop:zipcode.label')}
          currentValue={workshop?.zipcode}
          newValue={newValues?.zipcode}
          oldValue={oldValues?.zipcode}
          showDiff={showDiff}
        />

        <DetailField
          label={t('workshop:country.label')}
          currentValue={workshop?.country}
          newValue={newValues?.country}
          oldValue={oldValues?.country}
          showDiff={showDiff}
          translate={(val) => t(`location:countries.${val.toUpperCase()}`)}
        />

        <DetailField
          label={t('workshop:firstname.label')}
          currentValue={workshop?.firstname}
          newValue={newValues?.firstname}
          oldValue={oldValues?.firstname}
          showDiff={showDiff}
        />

        <DetailField
          label={t('workshop:lastname.label')}
          currentValue={workshop?.lastname}
          newValue={newValues?.lastname}
          oldValue={oldValues?.lastname}
          showDiff={showDiff}
        />

        <DetailField
          label={t('workshop:email.label')}
          currentValue={workshop?.email}
          newValue={newValues?.email}
          oldValue={oldValues?.email}
          showDiff={showDiff}
        />

        <DetailField
          label={t('workshop:telephone.label')}
          currentValue={workshop?.telephone}
          newValue={newValues?.telephone}
          oldValue={oldValues?.telephone}
          showDiff={showDiff}
        />
      </SimpleGrid>
    </HStack>
  );
};

export default VerificationDetailsGrid;
